import { ToastService } from './../../shared/components/toast/toast.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

import { User } from 'src/app/models/User';
import { Token, TwoFactorChallenge, isTwoFactorChallenge } from 'src/app/models/Token';
import { AuthService } from './../../shared/services/auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {

  get supportLink() {
    return environment.supportLink + '?text=Minha conta foi bloqueada por tentativas de acesso com senha inválidas.';
  }

  /**
   * Mesmo destino do item Suporte do menu (CU-86ak84mj6). O texto vai
   * preenchido com o e-mail digitado para o atendimento já saber de qual conta
   * se trata — o usuário não está logado neste ponto, então é o único
   * identificador que temos.
   */
  get twoFactorSupportLink() {
    const email = this.loginForm?.get('email')?.value ?? '';

    return environment.supportLink
      + '?text=' + encodeURIComponent(
        'Olá, preciso ativar a verificação em duas etapas para acessar minha conta'
        + (email ? ` (${email})` : '') + '.'
      );
  }

  get registerLink() {
    if (sessionStorage.getItem('app_platform_id') !== null) {
      return environment.registerLink;
    }

    return false
  }

  title = '';
  loginForm: UntypedFormGroup;
  passwordAttemptsError = false;
  utmParams: string;

  /**
   * CU-86ak84mj6 — passo atual da tela.
   *  credentials → e-mail e senha
   *  code        → autenticador já configurado, só falta o código
   *
   * Não existe passo de cadastro aqui: o autenticador só é configurado de dentro
   * do painel, com sessão válida e senha confirmada (tela de perfil).
   */
  step: 'credentials' | 'code' = 'credentials';
  twoFactorToken: string = null;
  twoFactorCode = '';
  recoveryCode = '';
  usingRecoveryCode = false;
  twoFactorError: string = null;

  /** Submissão em curso — trava o botão e o Enter repetido. */
  busy = false;

  /**
   * Perfil obrigado ao segundo fator (Administrador ou Customer Success) sem
   * autenticador cadastrado: a mensagem vem do backend e a tela só acrescenta o
   * link do Suporte.
   */
  twoFactorBlockedMessage: string = null;

  private subscriptions: Subscription[] = [];

  @ViewChild('inputEmail') inputEmail: ElementRef<HTMLInputElement>;

  constructor(
    private authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    if ( !['app.smsconvert.com', 'app.smsfunnel.com.br'].includes(window.location.host) ) {
      this.title = 'Login';
      window.document.title = 'Login - SMS Funnel';
    }

    if (this.authService.isLogged()) {
      this.router.navigate(['/']);
      return;
    }

    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      utm: ['']
    });

    this.subscriptions.push(this.queryParams());

    if (sessionStorage.getItem('app_platform_id') !== null) {
      const referrer = document.referrer;

      if (referrer === environment.registerLink) {
        this.toastService.success('Cadastro realizado com sucesso!<br>Efetue o login para continuar.', 10000);
      }
    }
  }

  ngAfterViewInit(): void {
    this.focusEmail();
  }

  /**
   * O cursor volta para o e-mail — ao abrir a tela e ao barrar um envio inválido.
   * Era o `ngAfterViewInit()` chamado na mão de dentro do `login()`; com a guarda
   * consertada isso virou código vivo, e hook de ciclo de vida chamado à mão
   * esconde o que a linha faz.
   */
  private focusEmail(): void {
    setTimeout(() => {
      this.inputEmail?.nativeElement?.focus();
    }, 200);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  queryParams(): Subscription {
    return this.route.queryParams.subscribe(params => {
      const allEntries = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`);

      this.utmParams = `?${allEntries.join('&')}`;
      this.loginForm.patchValue({ utm: this.utmParams });
    });
  }

  login(): void {
    // Sem trava, segurar Enter dispara N chamadas: queima o limite do backend e,
    // no passo do código, pode consumir mais de um código de recuperação.
    if (this.busy) {
      return;
    }

    // `&&` aqui era sempre falso — com o form existindo o `!form` curto-circuita,
    // e sem ele o `.valid` lançaria antes do toast. A guarda nunca rodava, então
    // e-mail malformado ia para o backend, queimava tentativa do limite por conta
    // e por IP (CU-86ak84mj6) e voltava como "Usuário/Senha inválido(s)!".
    if (!this.loginForm || !this.loginForm.valid) {
      this.toastService.error('Preencha um E-mail e Senha válidos para acessar!');
      this.focusEmail();
      return;
    }

    this.twoFactorBlockedMessage = null;

    const user = this.loginForm.getRawValue() as User;
    this.busy = true;
    this.authService
      .login(user)
      .subscribe(
        response => {
          this.busy = false;

          // Senha correta ainda pode não ser sessão: o backend responde com um
          // desafio quando o perfil exige (ou o usuário ativou) segundo fator.
          if (isTwoFactorChallenge(response)) {
            this.startTwoFactor(response as TwoFactorChallenge);
            return;
          }

          this.router.navigate(['/']);
        },
        err => {
          this.busy = false;
          const { status, error } = err;

          // Perfil obrigado ao segundo fator (Administrador ou Customer Success)
          // sem autenticador: a mensagem vem do backend e a tela só acrescenta o
          // link do Suporte.
          if (status === 403 && error?.error === 'two_factor_required') {
            this.twoFactorBlockedMessage = error.message;
            this.passwordAttemptsError = false;
            return;
          }

          if (error.user_blocked) {
            // O backend passou a mandar `user_blocked` só quando a conta está
            // DESATIVADA — antes vinha de `login_attempts > 3` e a tela anunciava
            // bloqueio para quem só precisava esperar o castigo passar.
            this.toastService.error('Sua conta está desativada. Fale com o Suporte.');
            this.passwordAttemptsError = true;
            return;
          }

          if (status === 429) {
            this.toastService.error(
              error?.message || 'Muitas tentativas. Tente novamente em alguns minutos.'
            );
            this.passwordAttemptsError = true;
            return;
          }

          if (status !== 0 && !error.user_blocked) {
            this.toastService.error('Usuário/Senha inválido(s)!');
            this.passwordAttemptsError = false;
          }
        }
      );
  }

  private startTwoFactor(challenge: TwoFactorChallenge): void {
    this.twoFactorToken = challenge.two_factor_token;
    this.twoFactorError = null;
    this.twoFactorCode = '';
    this.step = 'code';
  }

  /** Passo `code`: usuário que já tem o autenticador configurado. */
  verifyCode(): void {
    if (this.busy) {
      return;
    }

    const codigo = this.usingRecoveryCode ? this.recoveryCode : this.twoFactorCode;

    // Submissão vazia consumia tentativa e queimava o limite do backend.
    if (!codigo || !codigo.trim()) {
      this.twoFactorError = this.usingRecoveryCode
        ? 'Digite um código de recuperação.'
        : 'Digite o código de 6 dígitos do aplicativo.';
      return;
    }

    if (!this.usingRecoveryCode && !/^\d{6}$/.test(codigo.trim())) {
      this.twoFactorError = 'O código do aplicativo tem 6 dígitos.';
      return;
    }

    this.twoFactorError = null;
    this.busy = true;

    this.authService
      .verifyTwoFactor(
        this.twoFactorToken,
        this.twoFactorCode,
        this.usingRecoveryCode ? this.recoveryCode : null
      )
      .subscribe(
        () => {
          this.busy = false;
          this.router.navigate(['/']);
        },
        err => {
          this.busy = false;
          this.handleTwoFactorError(err);
        }
      );
  }

  toggleRecoveryCode(): void {
    this.usingRecoveryCode = !this.usingRecoveryCode;
    this.twoFactorError = null;
  }

  private handleTwoFactorError(err: any): void {
    // 401 aqui é desafio expirado ou inválido — não adianta tentar de novo sem
    // refazer o login.
    if (err.status === 401) {
      this.failTwoFactor('Sua sessão de login expirou. Entre novamente.');
      return;
    }

    if (err.status === 429) {
      this.twoFactorError = 'Muitas tentativas. Aguarde um instante antes de tentar de novo.';
      return;
    }

    // O backend distingue código errado de código já consumido, e as ações são
    // opostas: conferir o app × esperar o próximo. Usa a mensagem de lá.
    if (err.status === 422 && err.error?.error) {
      this.twoFactorError = err.error.error;
      return;
    }

    this.twoFactorError = this.usingRecoveryCode
      ? 'Código de recuperação inválido.'
      : 'Código inválido. Confira o aplicativo autenticador.';
  }

  private failTwoFactor(message: string): void {
    this.toastService.error(message);
    this.cancelTwoFactor();
  }

  cancelTwoFactor(): void {
    this.step = 'credentials';
    this.twoFactorToken = null;
    this.twoFactorCode = '';
    this.recoveryCode = '';
    this.usingRecoveryCode = false;
    this.twoFactorError = null;
    this.loginForm.patchValue({ password: '' });
  }

}
